import { useEffect, useState } from "react";
import { MessageSquarePlus, Instagram, ArrowUp, ArrowDown, ChevronsUp } from "lucide-react";
import { api, errMsg, money, usd, photoUrl } from "@/lib/api";
import { useAuth } from "./AuthContext";
import ResourcePage, { PageTitle, Btn } from "./ResourcePage";
import { PROP_STATUS, LEAD_STATUS, label } from "@/lib/constants";
import * as R from "./resources";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const Badge = ({ children }) => <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-slate-100 capitalize">{children}</span>;

function ImportInstagram({ onDone }) {
  const [open, setOpen] = useState(false);
  const [url, setUrl] = useState("");
  const [status, setStatus] = useState("disponible");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/instagram/import", { url, status });
      toast.success(`Importada como ${r.data.code}: ${r.data.title || ""} (${r.data.photos?.length || 0} fotos)`);
      setUrl("");
      setOpen(false);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <>
      <Btn variant="ghost" onClick={() => setOpen(true)} data-testid="properties-import-instagram-button"><Instagram size={15} />Importar de Instagram</Btn>
      <Dialog open={open} onOpenChange={(o) => !busy && setOpen(o)}>
        <DialogContent data-testid="import-instagram-dialog">
          <DialogHeader><DialogTitle>Importar publicación de Instagram</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-500">Pega el enlace de la publicación. Se copian todas las fotos y los datos que aparecen en el texto (no se inventa nada). Revisa la ficha después de importar.</p>
            <Input placeholder="https://www.instagram.com/p/..." value={url} onChange={(e) => setUrl(e.target.value)} data-testid="import-instagram-url" />
            <select className="w-full h-10 border rounded-md px-3 text-sm" value={status} onChange={(e) => setStatus(e.target.value)} data-testid="import-instagram-status">
              <option value="disponible">Publicar como Disponible</option><option value="borrador">Guardar como Borrador (revisar antes)</option><option value="alquilada">Marcar como Alquilada</option>
            </select>
            <Btn onClick={submit} disabled={busy || !url.trim()} data-testid="import-instagram-submit">{busy ? "Importando... (puede tardar 30 s)" : "Importar"}</Btn>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

export function PropertiesAdmin() {
  const [k, setK] = useState(0);
  const [moving, setMoving] = useState(false);
  const move = async (r, direction) => {
    if (moving) return;
    setMoving(true);
    try { const res = await api.post(`/admin/properties/${r.id}/move`, { direction }); toast.success(`${r.code} ahora en posición ${res.data.position}`); setK((x) => x + 1); } catch (e) { toast.error(errMsg(e)); } finally { setMoving(false); }
  };
  const mv = (r, d, Icon, title) => <button disabled={moving} onClick={() => move(r, d)} className="inline-flex p-1.5 hover:text-[#FF3333] disabled:opacity-30" title={title} data-testid={`properties-move-${d}-${r.id}`}><Icon size={15} /></button>;
  return <ResourcePage name="properties" title="Propiedades" subtitle="Las nuevas aparecen primero. Usa las flechas para cambiar el orden en que se muestran en la web." fields={R.PROPERTY_FIELDS} reloadKey={k} sort="sort_order"
    toolbar={<ImportInstagram onDone={() => setK(k + 1)} />}
    defaults={{ status: "borrador", currency: "USD", furnished: "amueblado", photos: [] }}
    columns={[
      { key: "photo", label: "", render: (r) => <img src={photoUrl(r.photos?.[0])} alt="" className="h-10 w-14 rounded object-cover bg-slate-100" /> },
      { key: "code", label: "Código", render: (r) => <span className="font-mono-num text-xs">{r.code}</span> }, { key: "title", label: "Título" },
      { key: "zone", label: "Zona" }, { key: "price", label: "Precio", render: (r) => (r.price > 0 ? money(r.price, r.currency) : "—") },
      { key: "status", label: "Estado", render: (r) => <Badge>{label(PROP_STATUS, r.status)}</Badge> }]}
    rowActions={(r) => <>{mv(r, "top", ChevronsUp, "Mover al primer lugar")}{mv(r, "up", ArrowUp, "Subir")}{mv(r, "down", ArrowDown, "Bajar")}</>} />;
}

function LeadHistory({ lead, setLead }) {
  const [text, setText] = useState("");
  const add = async () => {
    if (!text.trim()) return;
    try { const r = await api.post(`/admin/leads/${lead.id}/note`, { text }); setLead({ ...lead, history: r.data.history }); setText(""); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="space-y-3 border-t pt-4" data-testid="lead-history">
      <h4 className="font-semibold text-sm">Historial</h4>
      <div className="flex gap-2"><Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Añadir seguimiento..." data-testid="lead-note-input" /><Btn type="button" onClick={add} data-testid="lead-note-add"><MessageSquarePlus size={14} /></Btn></div>
      <ul className="space-y-2 max-h-64 overflow-y-auto">{[...(lead.history || [])].reverse().map((h, i) => <li key={i} className="text-sm bg-slate-50 rounded-md p-2"><span className="text-xs text-slate-400">{h.at?.slice(0, 16).replace("T", " ")} · {h.by}</span><p>{h.text}</p></li>)}</ul>
    </div>
  );
}

function Kanban({ reloadKey, onChanged }) {
  const [leads, setLeads] = useState([]);
  useEffect(() => { api.get("/admin/c/leads").then((r) => setLeads(r.data)); }, [reloadKey]);
  const move = async (lead, status) => {
    let lost_reason = lead.lost_reason;
    if (status === "perdido") { lost_reason = window.prompt("Motivo de pérdida:") || ""; }
    try { await api.put(`/admin/c/leads/${lead.id}`, { status, lost_reason }); setLeads(leads.map((l) => (l.id === lead.id ? { ...l, status } : l))); onChanged(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <div className="flex gap-4 overflow-x-auto pb-4" data-testid="crm-kanban">
      {LEAD_STATUS.map((s) => (
        <div key={s.value} className="w-64 shrink-0 bg-slate-100/80 rounded-2xl p-3" onDragOver={(e) => e.preventDefault()}
          onDrop={(e) => { const l = leads.find((x) => x.id === e.dataTransfer.getData("id")); if (l && l.status !== s.value) move(l, s.value); }} data-testid={`kanban-col-${s.value}`}>
          <p className="text-xs font-bold uppercase tracking-wide text-slate-500 mb-3 px-1">{s.label} · {leads.filter((l) => (l.status || "nuevo") === s.value).length}</p>
          <div className="space-y-2">
            {leads.filter((l) => (l.status || "nuevo") === s.value).map((l) => (
              <div key={l.id} draggable onDragStart={(e) => e.dataTransfer.setData("id", l.id)} className="bg-white rounded-xl p-3 shadow-sm border border-slate-200 cursor-grab" data-testid={`kanban-card-${l.id}`}>
                <p className="font-semibold text-sm">{l.name}</p><p className="text-xs text-slate-500">{l.lead_type} · {l.source}</p>
                {l.next_action && <p className="text-xs text-[#FF3333] mt-1">{l.next_action} {l.next_action_date}</p>}
                <select value={l.status || "nuevo"} onChange={(e) => move(l, e.target.value)} className="mt-2 w-full text-xs border rounded-md px-1 py-1" data-testid={`kanban-move-${l.id}`}>
                  {LEAD_STATUS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}

export function CrmAdmin() {
  const [k, setK] = useState(0);
  return (
    <div data-testid="crm-page">
      <PageTitle title="CRM inmobiliario" subtitle="Pipeline: Nuevo → Contactado → Calificado → Visita → Negociación → Contrato → Cerrado / Perdido" />
      <Tabs defaultValue="pipeline">
        <TabsList><TabsTrigger value="pipeline" data-testid="crm-tab-pipeline">Pipeline</TabsTrigger><TabsTrigger value="list" data-testid="crm-tab-list">Leads</TabsTrigger><TabsTrigger value="tasks" data-testid="crm-tab-tasks">Tareas</TabsTrigger></TabsList>
        <TabsContent value="pipeline" className="mt-6"><Kanban reloadKey={k} onChanged={() => setK(k + 1)} /></TabsContent>
        <TabsContent value="list" className="mt-6">
          <ResourcePage name="leads" title="Leads" hideHeader fields={R.LEAD_FIELDS} defaults={{ status: "nuevo", source: "manual", lead_type: "inquilino" }} reloadKey={k} onSaved={() => setK(k + 1)}
            columns={[{ key: "created_at", label: "Fecha", render: (r) => r.created_at?.slice(0, 10) }, { key: "name", label: "Nombre" }, { key: "phone", label: "Teléfono" },
              { key: "lead_type", label: "Tipo" }, { key: "source", label: "Origen" }, { key: "zone", label: "Zona" },
              { key: "status", label: "Estado", render: (r) => <Badge>{label(LEAD_STATUS, r.status)}</Badge> }, { key: "next_action_date", label: "Próx. acción" }]}
            extra={(lead, setLead) => <LeadHistory lead={lead} setLead={setLead} />} />
        </TabsContent>
        <TabsContent value="tasks" className="mt-6">
          <ResourcePage name="tasks" title="Tareas" hideHeader fields={R.TASK_FIELDS} columns={[{ key: "title", label: "Tarea" }, { key: "due_date", label: "Fecha" }, { key: "done", label: "Hecha", render: (r) => (r.done ? "Sí" : "No") }]} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

export const OwnersAdmin = () => <ResourcePage name="owners" title="Propietarios" fields={R.OWNER_FIELDS} columns={[{ key: "name", label: "Nombre" }, { key: "phone", label: "Teléfono" }, { key: "email", label: "Correo" }, { key: "commission_terms", label: "Comisión" }]} />;
export const AgentsAdmin = () => <ResourcePage name="agents" title="Agentes" fields={R.AGENT_FIELDS} defaults={{ kind: "interno" }} columns={[{ key: "name", label: "Nombre" }, { key: "company", label: "Empresa" }, { key: "kind", label: "Tipo" }, { key: "phone", label: "Teléfono" }, { key: "default_split_pct", label: "Split %" }]} />;

function CollectDialog({ deal, onClose, onDone }) {
  const [f, setF] = useState({ amount: deal.calc?.pending || 0, date: new Date().toISOString().slice(0, 10), currency: "USD", exchange_rate: 1 });
  const submit = async () => {
    try { await api.post(`/admin/deals/${deal.id}/collect`, { ...f, amount: Number(f.amount), exchange_rate: Number(f.exchange_rate) }); toast.success("Cobro registrado en contabilidad"); onDone(); onClose(); } catch (e) { toast.error(errMsg(e)); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="collect-dialog"><DialogHeader><DialogTitle>Registrar cobro — {deal.title}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <p className="text-sm text-slate-500">Pendiente de cobro: {usd(deal.calc?.pending)}. Se creará un ingreso cobrado en Contabilidad.</p>
          <Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} data-testid="collect-amount" />
          <Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="collect-date" />
          <select className="w-full h-10 border rounded-md px-3 text-sm" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}><option>USD</option><option>DOP</option></select>
          {f.currency === "DOP" && <Input type="number" placeholder="Tasa DOP por USD" value={f.exchange_rate} onChange={(e) => setF({ ...f, exchange_rate: e.target.value })} data-testid="collect-rate" />}
          <Btn onClick={submit} data-testid="collect-submit">Registrar cobro</Btn>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function DealsAdmin() {
  const { can } = useAuth();
  const [collect, setCollect] = useState(null);
  const [k, setK] = useState(0);
  return (
    <>
      <ResourcePage name="deals" title="Negociaciones y comisiones" subtitle="Comisión bruta, split, agentes y neto se calculan automáticamente. Lo pendiente no cuenta como cobrado." fields={R.DEAL_FIELDS}
        defaults={{ operation: "directa", commission_months: 1, currency: "USD", status: "en_curso", billing_status: "pendiente", agents: [] }} reloadKey={k}
        columns={[{ key: "title", label: "Negociación" }, { key: "rent_price", label: "Renta", render: (r) => money(r.rent_price, r.currency) }, { key: "operation", label: "Operación" },
          { key: "calc.gross", label: "Bruta", render: (r) => usd(r.calc?.gross) }, { key: "calc.srpc", label: "SoloRentaPC", render: (r) => usd(r.calc?.srpc) },
          { key: "calc.external", label: "Externos", render: (r) => usd(r.calc?.external) }, { key: "calc.net", label: "Neta est.", render: (r) => <b>{usd(r.calc?.net)}</b> },
          { key: "calc.collected", label: "Cobrado", render: (r) => usd(r.calc?.collected) }, { key: "collection_status", label: "Cobro", render: (r) => <Badge>{r.collection_status}</Badge> },
          { key: "billing_status", label: "Factura" }]}
        rowActions={(r) => can("finance") && r.calc?.pending > 0 && <button onClick={() => setCollect(r)} className="text-xs font-semibold text-emerald-700 mr-2" data-testid={`deals-collect-${r.id}`}>Cobrar</button>} />
      {collect && <CollectDialog deal={collect} onClose={() => setCollect(null)} onDone={() => setK(k + 1)} />}
    </>
  );
}
